// Copy, cut and paste (`ScriveningsSession.markdown(for:)`/`plainText(for:)`, `EditingRules.pasting`). A copy carries
// the text twice: plain text for other apps (what the reader sees, no hidden syntax), and the editor's own Markdown
// under its own type, self-contained (emphasis opened where it starts inside a style and closed at its end), fragments
// a paragraph apart and dividers left out. Pasting the editor's Markdown keeps it exactly; plain text becomes prose.
import { EditorSelection, Transaction, type EditorState, type TransactionSpec } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { wordCount } from '@writing-suite/core';
import { selfContained, strippingInlineMarkers } from './markdown.ts';
import { pasting, proseFromPlainText } from './rules.ts';
import { pastedScript } from './screenplay.ts';
import { bodyWindow, windowAround } from './session.ts';
import { Exact, ExactCaret, layoutOf } from './state.ts';

/** The pasteboard type marking text copied from a scrivenings editor as its Markdown (`markdownPasteboardType`). */
export const MARKDOWN_MIME = 'application/x-writing-suite-markdown';

/** The selection as the editor's Markdown and as plain text; null when nothing is selected. */
export function copied(state: EditorState): { markdown: string; plain: string } | null {
  const layout = layoutOf(state);
  const md: string[] = [], plain: string[] = [];
  for (const r of state.selection.ranges) {
    if (r.empty) continue;
    const parts = layout.plan(r.from, r.to, '').filter((e) => e.to > e.from);
    const m: string[] = [], p: string[] = [];
    for (const e of parts) {
      if (layout.isScreenplay(e.index)) {
        const raw = state.doc.sliceString(e.from, e.to);
        m.push(raw);
        p.push(raw);
        continue;
      }
      const { w, base } = windowAround(state, e.index, e.from, e.to);
      const s = selfContained(w.t, e.from - base, e.to - base);
      m.push(s);
      p.push(strippingInlineMarkers(s));
    }
    md.push(m.join('\n\n'));
    plain.push(p.join('\n\n'));
  }
  return md.length ? { markdown: md.join('\n'), plain: plain.join('\n') } : null;
}

/** The words in the selection (`EditorController.selectedWords`), counted as the open text is: each fragment's part as
 *  its self-contained Markdown parsed (so list markers, scene breaks and fences aren't words), a script's part by its
 *  printed text; dividers left out. Null when nothing is selected, or the selection holds no words. */
export function selectedWords(state: EditorState): number | null {
  const layout = layoutOf(state);
  let words = 0;
  for (const r of state.selection.ranges) {
    if (r.empty) continue;
    for (const e of layout.plan(r.from, r.to, '').filter((e) => e.to > e.from)) {
      if (layout.isScreenplay(e.index)) {
        words += wordCount(state.doc.sliceString(e.from, e.to), 'screenplay');
        continue;
      }
      const { w, base } = windowAround(state, e.index, e.from, e.to);
      words += wordCount(selfContained(w.t, e.from - base, e.to - base));
    }
  }
  return words > 0 ? words : null;
}

/** Pasting `text` over the selection (`readSelection(from:)`): the editor's own Markdown (`markdown`) exactly, plain
 *  text as prose, a screenplay's as Fountain. Within one fragment the edit is made here, balanced with the styles
 *  around it; across fragments it's typed, and the filter splits it. */
export function pasteSpec(state: EditorState, text: string, markdown: boolean): TransactionSpec {
  const layout = layoutOf(state);
  const r = state.selection.main;
  const i = layout.within(r.from, r.to);
  const annotations = [Exact.of(true), ExactCaret.of(true), Transaction.userEvent.of('input.paste')];
  if (i >= 0) {
    if (layout.isScreenplay(i)) {
      const script = pastedScript(text);
      return { changes: { from: r.from, to: r.to, insert: script }, selection: EditorSelection.cursor(r.from + script.length), annotations, scrollIntoView: true };
    }
    // Plain text into verse looks for the poem's fence, which may be further up than the window reaches.
    const { w, base } = markdown ? windowAround(state, i, r.from, r.to) : bodyWindow(state, i);
    const f = pasting(w, text, markdown, r.from - base, r.to - base);
    return {
      changes: { from: f.from + base, to: f.to + base, insert: f.text }, selection: EditorSelection.cursor(f.caret + base),
      annotations, scrollIntoView: true,
    };
  }
  const screenplay = layout.isScreenplay(layout.bodyAt(r.from));
  const insert = screenplay ? pastedScript(text) : markdown ? text : proseFromPlainText(text);
  return { changes: { from: r.from, to: r.to, insert }, selection: EditorSelection.cursor(r.from + insert.length), userEvent: 'input.paste', scrollIntoView: true };
}

/** The last copy from any editor on this page: some browsers drop a custom type, and the plain text then finds it. */
let lastCopy: { markdown: string; plain: string } | null = null;

/** The DOM handlers for copy, cut and paste. */
export const clipboard = EditorView.domEventHandlers({
  copy(event, view) { return write(event, view, false); },
  cut(event, view) { return write(event, view, true); },
  paste(event, view) {
    const data = event.clipboardData;
    if (!data || view.state.readOnly) return false;
    const plain = data.getData('text/plain');
    let markdown: string | null = data.getData(MARKDOWN_MIME) || null;
    if (markdown === null && lastCopy && plain === lastCopy.plain) markdown = lastCopy.markdown;
    const text = markdown ?? plain;
    if (!text) return false;
    event.preventDefault();
    view.dispatch(pasteSpec(view.state, text, markdown !== null));
    return true;
  },
});

function write(event: ClipboardEvent, view: EditorView, cut: boolean): boolean {
  const data = event.clipboardData;
  const copy = copied(view.state);
  if (!data || !copy) return false;
  event.preventDefault();
  data.clearData();
  data.setData('text/plain', copy.plain);
  data.setData(MARKDOWN_MIME, copy.markdown);
  lastCopy = copy;
  if (cut && !view.state.readOnly) {
    const r = view.state.selection.main;
    view.dispatch({ changes: { from: r.from, to: r.to }, selection: EditorSelection.cursor(r.from), userEvent: 'delete.cut', scrollIntoView: true });
  }
  return true;
}
